import api from './api';

export const transcriptService = {
    /**
     * Start transcript generation (now asynchronous)
     */
    startTranscriptGeneration: async (employeeIds?: string[]) => {
        const response = await api.post('/admin/transcripts/download', {
            employeeIds: employeeIds || []
        });
        return response.data;
    },

    /**
     * Check transcript generation status
     */
    checkStatus: async (jobId: string) => {
        const response = await api.get(`/admin/transcripts/status/${jobId}`);
        return response.data;
    },

    /**
     * Get transcript info (returns JSON with download URLs)
     */
    getTranscriptInfo: async (jobId: string) => {
        const response = await api.get(`/admin/transcripts/download/${jobId}`);
        return response.data;
    },

    /**
     * Download completed transcript file
     */
    downloadTranscript: async (jobId: string, filename: string): Promise<Blob> => {
        try {
            const response = await api.get(`/admin/transcripts/download/${jobId}/${filename}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error: any) {
            // Handle blob error responses - they need special handling
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    const errorMessage = new Error(errorData.message || 'Failed to download transcript');
                    (errorMessage as any).response = error.response;
                    throw errorMessage;
                } catch (parseError) {
                    // If parsing fails, throw original error
                    throw error;
                }
            }
            throw error;
        }
    },

    /**
     * Start organization transcript generation (bulk download)
     */
    startOrganizationTranscriptGeneration: async (year: string) => {
        const response = await api.post('/admin/transcripts/bulk/download', {
            year: year
        });
        return response.data;
    },

    /**
     * Check organization transcript generation status
     */
    checkOrganizationTranscriptStatus: async (jobId: string) => {
        const response = await api.get(`/admin/transcripts/bulk/status/${jobId}`);
        return response.data;
    },

    /**
     * Download organization transcript file
     */
    downloadOrganizationTranscript: async (jobId: string): Promise<{ blob: Blob; filename: string }> => {
        try {
            const response = await api.get(`/admin/transcripts/bulk/download/${jobId}`, {
                responseType: 'blob'
            });
            
            // Extract filename from Content-Disposition header or URL
            let filename = `organization-transcript-${new Date().getFullYear()}.xlsx`; // fallback
            
            // Try different case variations of the header (axios normalizes headers to lowercase)
            const contentDisposition = response.headers['content-disposition'];
                    
            if (contentDisposition) {
                // Handle escaped quotes in filename: filename="...\"filename\"..."
                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            } else {
                // Fallback: extract from jobId if it contains the filename pattern
                // jobId format: bulk_org_transcript_1758948419429_af1wnttds
                if (jobId.startsWith('bulk_org_transcript_')) {
                    const timestamp = jobId.split('_')[3]; // Extract timestamp
                    if (timestamp && !isNaN(parseInt(timestamp))) {
                        // Convert timestamp to date
                        const date = new Date(parseInt(timestamp));
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        filename = `Organization_Transcript_${year}_${year}-${month}-${day}.xlsx`;
                    } else {
                        // Fallback to current date if timestamp parsing fails
                        const date = new Date().toISOString().split('T')[0];
                        filename = `Organization_Transcript_${new Date().getFullYear()}_${date}.xlsx`;
                    }
                }
            }
                    
            return { blob: response.data, filename };
        } catch (error: any) {
            // Handle blob error responses - they need special handling
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    const errorMessage = new Error(errorData.message || 'Failed to download organization transcript');
                    (errorMessage as any).response = error.response;
                    throw errorMessage;
                } catch (parseError) {
                    // If parsing fails, throw original error
                    throw error;
                }
            }
            throw error;
        }
    }
};

/**
 * Helper function to trigger file download
 */
export const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
